"use client";

import { useApiGet } from "@/hooks/use-api";
import { integrationKeys } from "./keys";
import type { CalendarConnectionStatus } from "./types";

/**
 * useCalendarStatus
 *
 * Returns the Google Calendar connection status for a given workspace.
 * Because all callers share the same React Query cache key, the HTTP request
 * is made at most once per staleTime window — even if the sidebar, the home
 * page, and the settings modal all call this hook simultaneously.
 *
 * @param workspaceId - The workspace to check the calendar connection for.
 *   Pass an empty string / undefined to skip the fetch (query is disabled).
 *
 * Usage:
 *   const { connected, userEmail, isLoading } = useCalendarStatus(workspaceId);
 */
export function useCalendarStatus(workspaceId: string | undefined) {
  const safeId = workspaceId ?? "";

  const { data, isLoading, isError, refetch } = useApiGet<CalendarConnectionStatus>(
    integrationKeys.calendar(safeId),
    `/api/calendar/status?workspaceId=${encodeURIComponent(safeId)}`,
    undefined,
    {
      enabled: !!safeId,
      staleTime: 5 * 60 * 1000,   // 5 min — connection status doesn't change often
      refetchOnWindowFocus: false,
    },
  );

  return {
    connected: data?.connected ?? false,
    userEmail: data?.userEmail,
    calendarId: data?.calendarId,
    isLoading,
    isError,
    refetch,
  };
}
